import { useCallback, useRef } from 'react'
import { Upload } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useCreateModel } from './context'
import { ACCEPTED_IMAGE_TYPES, MAX_IMAGES, UPLOAD_GUIDANCE } from './constants'
import { readImageDimensions, detectIssues } from './validation'
import type { TrainingImage } from './types'

/** Full-size drop zone — only shown when no images uploaded yet */
export function UploadZone() {
  const { state, dispatch } = useCreateModel()
  const inputRef = useRef<HTMLInputElement>(null)

  const processFiles = useCallback(
    async (fileList: FileList | File[]) => {
      const files = Array.from(fileList).filter((f) => ACCEPTED_IMAGE_TYPES.includes(f.type))
      if (files.length === 0) return

      const remaining = MAX_IMAGES - state.images.length
      const toProcess = files.slice(0, remaining)
      const newImages: TrainingImage[] = []

      for (const file of toProcess) {
        try {
          const { width, height } = await readImageDimensions(file)
          const objectUrl = URL.createObjectURL(file)
          const issues = detectIssues(width, height, file.size, [...state.images, ...newImages], file.name)

          newImages.push({
            id: crypto.randomUUID(),
            file,
            objectUrl,
            name: file.name,
            width,
            height,
            sizeBytes: file.size,
            format: file.type.split('/')[1] ?? 'unknown',
            caption: '',
            captionStatus: 'none',
            issues,
          })
        } catch {
          // Skip files that fail to load
        }
      }

      if (newImages.length > 0) dispatch({ type: 'ADD_IMAGES', payload: newImages })
    },
    [state.images, dispatch],
  )

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault()
      dispatch({ type: 'SET_DRAGGING', payload: false })
      processFiles(e.dataTransfer.files)
    },
    [dispatch, processFiles],
  )

  return (
    <div
      onDragOver={(e) => { e.preventDefault(); dispatch({ type: 'SET_DRAGGING', payload: true }) }}
      onDragLeave={() => dispatch({ type: 'SET_DRAGGING', payload: false })}
      onDrop={handleDrop}
      onClick={() => inputRef.current?.click()}
      className={cn(
        'flex cursor-pointer flex-col items-center justify-center gap-3 rounded-lg border border-dashed py-14 transition-colors',
        state.isDragging ? 'border-primary bg-primary/5' : 'border-border',
      )}
    >
      <div className="flex h-10 w-10 items-center justify-center rounded-full bg-muted">
        <Upload className="h-4 w-4 text-muted-foreground" />
      </div>
      <div className="text-center">
        <p className="text-sm font-medium">{UPLOAD_GUIDANCE[state.modelType].title}</p>
        <p className="text-xs text-muted-foreground">{UPLOAD_GUIDANCE[state.modelType].hint}</p>
        <p className="mt-1 text-[11px] text-muted-foreground/60">
          PNG, JPEG, WebP &middot; Up to {MAX_IMAGES} files
        </p>
      </div>
      <input
        ref={inputRef}
        type="file"
        accept={ACCEPTED_IMAGE_TYPES.join(',')}
        multiple
        className="hidden"
        onChange={(e) => {
          if (e.target.files) {
            processFiles(e.target.files)
            e.target.value = ''
          }
        }}
      />
    </div>
  )
}
